import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, MessageSquare, PackageX, ChevronDown, ChevronUp, CheckCircle2, Clock } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
import { Screen } from '../types';
import { Header } from '../components/ui/Header';
import { Button } from '../components/ui/Button';
export function VendorDisputesScreen() {
  const {
    navigate
  } = useNavigation();
  const [activeTab, setActiveTab] = useState<'Open' | 'In Progress' | 'Resolved' | 'All'>('Open');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Mock disputes
  const disputes = [{
    id: 'DSP-001',
    orderId: 'VO-002',
    buyerName: 'Jane Smith',
    reason: 'Defective Product',
    date: '2024-03-15',
    status: 'Open',
    priority: 'High',
    message: 'The screen of the phone has a green line on the right side. I just opened it today.'
  }, {
    id: 'DSP-002',
    orderId: 'VO-005',
    buyerName: 'Michael Obi',
    reason: 'Not Delivered',
    date: '2024-03-12',
    status: 'In Progress',
    priority: 'Medium',
    message: 'Tracking says delivered but I have not received anything at my gate.'
  }, {
    id: 'DSP-003',
    orderId: 'VO-001',
    buyerName: 'John Doe',
    reason: 'Wrong Item',
    date: '2024-03-01',
    status: 'Resolved',
    priority: 'Low',
    message: 'I ordered black but received silver.'
  }];
  const filteredDisputes = disputes.filter(d => {
    if (activeTab === 'All') return true;
    return d.status === activeTab;
  });
  return <div className="min-h-full bg-gray-50 dark:bg-gray-900 pb-10" data-id="element-1865">
      <Header title="Disputes" showBack data-id="element-1866" />

      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10 overflow-x-auto scrollbar-hide" data-id="element-1867">
        <div className="flex min-w-max px-2" data-id="element-1868">
          {['Open', 'In Progress', 'Resolved', 'All'].map(tab => <button key={tab} onClick={() => setActiveTab(tab as any)} className={`px-4 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-1869">
              {tab}
            </button>)}
        </div>
      </div>

      <div className="p-4 space-y-4" data-id="element-1870">
        {filteredDisputes.length === 0 ? <div className="text-center py-12" data-id="element-1871">
            <AlertTriangle className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" data-id="element-1872" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-1873">
              No disputes found
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mt-1" data-id="element-1874">
              You have no {activeTab.toLowerCase()} disputes.
            </p>
          </div> : filteredDisputes.map(dispute => <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} key={dispute.id} className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden" data-id="element-1875">
              <div className="p-4 cursor-pointer flex items-start gap-3" onClick={() => navigate(Screen.VENDOR_DISPUTE_DETAIL, {
          disputeId: dispute.id
        })} data-id="element-1876">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${dispute.priority === 'High' ? 'bg-red-100 text-red-600 dark:bg-red-900/30' : dispute.priority === 'Medium' ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30' : 'bg-blue-100 text-blue-600 dark:bg-blue-900/30'}`} data-id="element-1877">
                  <PackageX className="w-5 h-5" data-id="element-1878" />
                </div>
                <div className="flex-1" data-id="element-1879">
                  <div className="flex justify-between items-start" data-id="element-1880">
                    <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-1881">
                      {dispute.reason}
                    </h3>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium uppercase ${dispute.status === 'Resolved' ? 'bg-green-100 text-green-700 dark:bg-green-900/30' : dispute.status === 'In Progress' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30' : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30'}`} data-id="element-1882">
                      {dispute.status}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1883">
                    Order #{dispute.orderId} • {dispute.buyerName}
                  </div>
                </div>
                {expandedId === dispute.id ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-1884" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-1885" />}
              </div>

              <AnimatePresence data-id="element-1886">
                {expandedId === dispute.id && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} className="overflow-hidden border-t border-gray-100 dark:border-gray-700" data-id="element-1887">
                    <div className="p-4 bg-gray-50 dark:bg-gray-900/50" data-id="element-1888">
                      <div className="mb-4" data-id="element-1889">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-1890">
                          Buyer's Message
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 p-3 rounded-xl border border-gray-100 dark:border-gray-700" data-id="element-1891">
                          "{dispute.message}"
                        </p>
                      </div>

                      {dispute.status !== 'Resolved' && <div className="space-y-3" data-id="element-1892">
                          <textarea rows={3} placeholder="Type your response..." className="w-full p-3 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-primary dark:text-white" data-id="element-1893"></textarea>
                          <div className="flex gap-2" data-id="element-1894">
                            <Button variant="outline" className="flex-1 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300" data-id="element-1895">
                              Accept Return
                            </Button>
                            <Button className="flex-1 bg-primary hover:bg-primary/90 text-white" data-id="element-1896">
                              Respond
                            </Button>
                          </div>
                        </div>}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
      </div>
    </div>;
}